/* eslint-disable @typescript-eslint/no-unused-vars */
import Event20 from '@carbon/icons-react/es/event/20';
import Export16 from '@carbon/icons-react/es/export/16';
import Maximize16 from '@carbon/icons-react/es/maximize/16';
import { ConfigProvider, Tooltip } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { Modal as ModalAtom } from 'components/atoms/modal';
import { Select as SelectAtom } from 'components/atoms/select';
import { ColumnHeader, TableSkeleton } from 'components/atoms/table';
import { StyledTable as UnderlyingProjectsTable } from 'components/organisms/module-settings/key-events-and-intervals/settings/styles';
import { DefaultEmptyKeyEventsAndIntervalsEmptyTable } from 'components/organisms/template-insight/empty-state';
import { ProductiveHeading07 } from 'components/styles';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useProjectsQuery } from 'queries';
import { useKeyEvents } from 'utils/project-fields/use-project-fields';
import { KeyEventsAndIntervalsChart, ChartTypes } from '../charts';
import { Table as DeviationTrendsTable } from '../table';
import { useKeyEventsReport } from '../use-key-events-report';
import { useExport } from './use-export';
import { computeKeyEventData, KeyEventPerformance } from './utils';

const groupBy = { monthly: 'MONTH', weekly: 'WEEK' };

const Wrapper = styled.div`
  overflow: auto;
  height: 100%;
  padding: var(--spacing-05);
  background: var(--coolest-gray-10);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--white-100);
  box-shadow: var(--shadow-02);
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: var(--spacing-05);
  align-items: center;
`;

const Header = styled.div`
  ${ProductiveHeading07};
  font-weight: var(--font-weight-bold) !important;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-05);
`;

const Select = styled(SelectAtom)`
  svg {
    color: var(--blue-60);
  }
  .ozone-select-container {
    display: inline-block !important;
  }
  .ozone-select__indicator-separator {
    background-color: transparent;
  }
  .ozone-select__control {
    width: 12rem;
    border: none;
    background: transparent;
    outline: none !important;
    cursor: pointer;
  }

  .ozone-select__single-value {
    font-weight: 700;
    color: var(--blue-60);
  }

  .ant-select-search__field {
    display: none;
  }
`;

const ExpandedDeviationTrendsTable = styled(DeviationTrendsTable)`
  max-height: none !important;
`;

const Modal = styled(ModalAtom)`
  width: 1200px !important;
  height: 300px !important;

  .ant-modal-header .ant-modal-title {
    font-size: var(--font-size-base);
  }
  .ant-modal-content {
    padding: var(--spacing-02);
  }
  .ant-modal-body {
    padding: 0;
    min-height: 300px;
  }
`;

export const KeyEventsView = ({
  filter,
  selectedRange,
  viewInitialized,
  zoomLevel,
}) => {
  const keyEventReportData = useKeyEventsReport(
    selectedRange,
    filter,
    groupBy[zoomLevel],
    viewInitialized,
  );

  const { data: metaData } = useProjectsQuery();

  const events = useKeyEvents();

  const translate = useTranslate();

  const { deviationChartOptions } = computeKeyEventData(
    keyEventReportData[1] ?? [],
    zoomLevel,
    translate,
  );

  const { exportAsCSV } = useExport(selectedRange, filter);

  const scrollToBottomRef = useRef<HTMLDivElement | null>(null);

  const scrollToUnderlyingProjects = () => {
    scrollToBottomRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const chartData = (deviationChartOptionsValue) => {
    const columns = [
      {
        title: '',
        children: [
          {
            title: (
              <FormattedMessage id="reports.KeyEvents.table.column_nameOfKeyEvent" />
            ),
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            width: 200,
          },
        ],
      },
      {
        title: (
          <FormattedMessage id="reports.keyEvents.table.column_title.overall" />
        ),
        children: [
          {
            title: (
              <FormattedMessage id="reports.keyEvents.table.column_noOfProjects" />
            ),
            dataIndex: 'noOfProjects',
            key: '',
            width: 150,
          },
          {
            title: (
              <FormattedMessage id="reports.keyEvents.table.column_median" />
            ),
            dataIndex: 'median',
            key: '',
            width: 150,
          },
          {
            title: (
              <FormattedMessage id="reports.keyEvents.table.column_average" />
            ),
            dataIndex: 'average',
            key: '',
            width: 150,
          },
        ],
      },
      {
        title:
          zoomLevel === 'monthly' ? (
            <FormattedMessage id="reports.keyEvents.table.column_monthlyBreakup" />
          ) : (
            <FormattedMessage id="reports.keyEvents.table.column_weeklyBreakup" />
          ),
        children: deviationChartOptionsValue.xAxis.categories.map((item) => ({
          title: item,
          key: item,
          dataIndex: item,
          width: 100,
        })),
      },
    ];

    const data = (keyEventReportData[1] ?? [])
      .filter((item) => Number(item.projectIds.length) > 0)
      .map((item) => {
        const sData =
          deviationChartOptionsValue.series.find(
            (item1) => item1.name === item.key,
          )?.data ?? [];
        const mappedData = {};
        const { categories } = deviationChartOptionsValue.xAxis;
        let index = 0;

        while (index < categories.length) {
          mappedData[categories[index]] = sData[index]
            ? (sData[index].y && (
                <span
                  style={{
                    color: `${
                      sData[index].y > 0 ? 'var(--red-60)' : 'var(--green-50)'
                    }`,
                  }}
                >
                  {`${Math.abs(sData[index].y)}d`}
                </span>
              )) ??
              '-'
            : '-';
          index += 1;
        }

        return {
          name: (
            <span
              style={{ color: 'var(--blue-60)', textDecoration: 'underline' }}
            >
              {item?.key}
            </span>
          ),
          nameValue: item?.key ?? '',
          noOfProjects: item?.projectIds?.length ?? '-',
          median: item.median ? `${item.median}d` : '-',
          average: item.average ? `${item.average}d` : '-',
          ...mappedData,
        };
      });

    return { chartColumn: columns, chartsData: data };
  };

  const { chartColumn, chartsData } = chartData(deviationChartOptions);

  const underlyingColumns = [
    {
      title: 'Name of the project',
      dataIndex: 'projectName',
      key: '',
    },
    { title: 'Planned date', dataIndex: 'plannedDate', key: '' },
    { title: 'Activity date', dataIndex: 'activityDate', key: '' },
    {
      title: (
        <ColumnHeader
          title="Performance"
          description="Calculated based on working days. Weekends will be skipped."
        />
      ),
      description: useTranslation('reports.columns.end_date.delay.tooltip'),
      dataIndex: 'performance',
      key: '',
    },
  ];

  const options = events.map((event) => ({
    label: event?.fieldLabel?.toString() ?? '',
    value: event.fieldId,
  }));

  const selectRef = useRef<any>(null);
  const [selectState, setSelectState] = useState(options[0] ? options[0] : {});
  const [initialized, setInitialized] = useState(false);
  const handleSelectOnChange = (e) => {
    setSelectState(e);
  };

  useEffect(() => {
    if (options.length > 0) {
      if (!initialized) {
        setSelectState(options[0]);
        setInitialized(true);
      }
    }
  }, [options, initialized]);

  const relatedFieldMapping = events.reduce((acc, curr) => {
    return { ...acc, [curr.fieldId]: curr?.metaData?.relatedFieldId };
  }, {});

  const underlyingData = useCallback(
    (_keyEventReportData, _selectState, _relatedFieldMapping) => {
      return (_keyEventReportData ?? [])
        ?.find((item) => item.startKeyEventId === _selectState.value)
        ?.projectIds.map((item) => {
          const { projectName, fields } =
            metaData?.find((project) => project.projectId === item) ?? {};

          const plannedDate = String(
            fields?.find(
              (field) =>
                field.fieldId === _relatedFieldMapping[_selectState.value],
            )?.fieldValue ?? '-',
          );

          const activityDate = String(
            fields?.find((field) => field.fieldId === _selectState.value)
              ?.fieldValue ?? '-',
          );

          return {
            projectName,
            activityDate:
              activityDate !== '-' ? (
                <HumanisedDate timestamp={moment(activityDate)} />
              ) : (
                '-'
              ),
            plannedDate:
              plannedDate !== '-' ? (
                <HumanisedDate timestamp={moment(plannedDate)} />
              ) : (
                '-'
              ),
            performance: (
              <KeyEventPerformance
                plannedDate={plannedDate}
                activityDate={activityDate}
              />
            ),
          };
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const calcData = underlyingData(
    keyEventReportData[1],
    selectState,
    relatedFieldMapping,
  );

  const [deviationModal, setDeviationModal] = useState(false);
  const [underlyingProjectsModal, setUnderlyingProjectsModal] = useState(false);

  const listOfKeyEventsTableTitle = useTranslation(
    'reports.keyEvents.list_of_key_events.table.title',
  );
  const underlyingProjectsOfKeyEvents = useTranslation(
    'reports.keyEvents.underlying_projects_of_key_events.table.title',
  );

  const isLoading = keyEventReportData[0] === 'loading';

  return (
    <Wrapper>
      <KeyEventsAndIntervalsChart
        chartType={ChartTypes.KeyEventAsDeviation}
        chartOptions={deviationChartOptions}
        isLoading={isLoading}
      />
      <TableWrapper>
        {isLoading ? (
          <TableSkeleton columnCount={8} />
        ) : (
          <>
            <HeaderWrapper>
              <Header>{listOfKeyEventsTableTitle}</Header>
              <IconWrapper>
                <Tooltip title={<FormattedMessage id="expand" />}>
                  <Button
                    icon={<Maximize16 />}
                    type="text"
                    size="small"
                    onClick={() => setDeviationModal(true)}
                  />
                </Tooltip>
                {/* <Tooltip
                  title={
                    <FormattedMessage id="reports.users_performance.export.tooltip" />
                  }
                >
                  <Button
                    icon={<Export16 />}
                    type="text"
                    size="small"
                    onClick={exportAsCSV}
                  />
                </Tooltip> */}
              </IconWrapper>
            </HeaderWrapper>
            <ConfigProvider
              renderEmpty={() =>
                DefaultEmptyKeyEventsAndIntervalsEmptyTable({
                  noDataDescriptionKey:
                    'reports.keyEvents.table.list_of_key_events.empty.description',
                })
              }
            >
              <DeviationTrendsTable
                columns={chartColumn}
                dataSource={chartsData}
                pagination={false}
                bordered
                scroll={chartsData?.length > 0 ? { x: 1300 } : {}}
                onRow={(record) => {
                  return {
                    onClick: () => {
                      const event = events.find(
                        (_event) =>
                          _event.fieldLabel === (record as any)?.nameValue ??
                          '',
                      );

                      setSelectState({
                        label: event?.fieldLabel,
                        value: event?.fieldId,
                      });
                      selectRef?.current?.select?.setValue({
                        label: event?.fieldLabel,
                        value: event?.fieldId,
                      });

                      scrollToUnderlyingProjects();
                    },
                  };
                }}
              />
            </ConfigProvider>
          </>
        )}
      </TableWrapper>
      <TableWrapper>
        {isLoading ? (
          <TableSkeleton columnCount={8} />
        ) : (
          <>
            <HeaderWrapper>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--blue-60)',
                    }}
                  >
                    <Event20 />
                    <Select
                      ref={selectRef}
                      onChange={handleSelectOnChange}
                      options={options}
                      defaultValue={selectState}
                    />
                  </div>
                  <Header>{underlyingProjectsOfKeyEvents}</Header>
                </div>
              </div>
              <Tooltip title={<FormattedMessage id="expand" />}>
                <Button
                  icon={<Maximize16 />}
                  type="text"
                  size="small"
                  onClick={() => setUnderlyingProjectsModal(true)}
                />
              </Tooltip>
            </HeaderWrapper>
            <ConfigProvider
              renderEmpty={() =>
                DefaultEmptyKeyEventsAndIntervalsEmptyTable({
                  noDataDescriptionKey:
                    'reports.keyEvents.table.underlying_projects_of_key_events.empty.description',
                })
              }
            >
              <UnderlyingProjectsTable
                columns={underlyingColumns}
                dataSource={calcData}
                pagination={false}
                scroll={calcData?.length > 0 ? { x: 1300, y: 300 } : {}}
                bordered
              />
            </ConfigProvider>
          </>
        )}
      </TableWrapper>
      <div ref={scrollToBottomRef} />
      {deviationModal && (
        <Modal
          visible={deviationModal}
          title={<b>{listOfKeyEventsTableTitle}</b>}
          footer={null}
          maskClosable={false}
          onCancel={() => setDeviationModal(false)}
          style={{ width: '1200px', height: '70px' }}
        >
          {' '}
          <>
            <ConfigProvider
              renderEmpty={() =>
                DefaultEmptyKeyEventsAndIntervalsEmptyTable({
                  noDataDescriptionKey:
                    'reports.keyEvents.table.list_of_key_events.empty.description',
                })
              }
            >
              <ExpandedDeviationTrendsTable
                columns={chartColumn}
                dataSource={chartsData}
                pagination={false}
                scroll={chartsData?.length > 0 ? { x: 1300 } : {}}
                bordered
                onRow={(record) => {
                  return {
                    onClick: () => {
                      const event = events.find(
                        (_event) =>
                          _event.fieldLabel === (record as any)?.nameValue ??
                          '',
                      );

                      setSelectState({
                        label: event?.fieldLabel,
                        value: event?.fieldId,
                      });
                      selectRef?.current?.select?.setValue({
                        label: event?.fieldLabel,
                        value: event?.fieldId,
                      });
                    },
                  };
                }}
              />
            </ConfigProvider>
          </>
        </Modal>
      )}
      {underlyingProjectsModal && (
        <Modal
          visible={underlyingProjectsModal}
          title={<b>{underlyingProjectsOfKeyEvents}</b>}
          footer={null}
          maskClosable={false}
          onCancel={() => setUnderlyingProjectsModal(false)}
          style={{ width: '1200px', height: '70px' }}
        >
          <>
            <ConfigProvider
              renderEmpty={() =>
                DefaultEmptyKeyEventsAndIntervalsEmptyTable({
                  noDataDescriptionKey:
                    'reports.keyEvents.table.underlying_projects_of_key_events.empty.description',
                })
              }
            >
              <UnderlyingProjectsTable
                columns={underlyingColumns}
                dataSource={calcData}
                pagination={false}
                scroll={calcData?.length > 0 ? { x: 1300 } : {}}
                bordered
              />
            </ConfigProvider>
          </>
        </Modal>
      )}
    </Wrapper>
  );
};
